import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { AppleIcon, GooglePlayIcon } from "@/components/StoreIcons";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";

export const StoreBadgesSection = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const joinWaitlist = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/waitlist`, { email });
      toast.success("You're on the list — see you at launch.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="download" className="relative py-28 sm:py-36 border-t border-white/5 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 left-[20%] w-[500px] h-[400px] rounded-full bg-ink-pink/10 blur-[150px]" />
        <div className="absolute top-0 right-[15%] w-[400px] h-[400px] rounded-full bg-ink-cyan/[0.07] blur-[140px]" />
      </div>

      <div className="relative max-w-4xl mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
        >
          <img src="/assets/icon.png" alt="IN-It app icon" className="w-24 h-24 rounded-[1.75rem] mx-auto mb-8 shadow-[0_20px_60px_rgba(255,27,130,0.3)]" />
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-ink-coral mb-4">COMING SOON TO IOS & ANDROID</p>
          <h2 className="font-display font-extrabold tracking-tighter text-white text-4xl sm:text-6xl mb-6">
            Get in the room <span className="text-brand-gradient">first.</span>
          </h2>
          <p className="text-zinc-400 max-w-lg mx-auto mb-10">
            IN-It is launching soon. Leave your email and we'll let you in before the doors open.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="flex flex-wrap justify-center gap-4 mb-12"
        >
          <div className="relative flex items-center gap-3.5 rounded-2xl border border-white/15 bg-black px-7 py-3.5">
            <AppleIcon className="w-9 h-9 text-white" />
            <span className="text-left">
              <span className="block text-[10px] text-zinc-400 leading-tight">Download on the</span>
              <span className="block text-xl font-semibold text-white leading-tight">App Store</span>
            </span>
            <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-gradient-to-r from-[#FF8A3D] to-[#FF3D3D] px-3 py-0.5 font-mono text-[9px] tracking-[0.2em] text-white">COMING SOON</span>
          </div>
          <div className="relative flex items-center gap-3.5 rounded-2xl border border-white/15 bg-black px-7 py-3.5">
            <GooglePlayIcon className="w-7 h-7" />
            <span className="text-left">
              <span className="block text-[10px] text-zinc-400 leading-tight">GET IT ON</span>
              <span className="block text-xl font-semibold text-white leading-tight">Google Play</span>
            </span>
            <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-gradient-to-r from-[#FF8A3D] to-[#FF3D3D] px-3 py-0.5 font-mono text-[9px] tracking-[0.2em] text-white">COMING SOON</span>
          </div>
        </motion.div>

        <motion.form
          onSubmit={joinWaitlist}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.25 }}
          className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
        >
          <input
            data-testid="hero-waitlist-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@email.com"
            className="flex-1 rounded-full bg-white/[0.05] border border-white/12 px-6 py-4 text-sm text-white placeholder:text-zinc-500 outline-none focus:border-ink-coral/60 transition-colors duration-200"
          />
          <button
            data-testid="hero-waitlist-submit"
            type="submit"
            disabled={loading}
            className="rounded-full bg-gradient-to-r from-[#FF8A3D] to-[#FF3D3D] text-white font-bold text-sm px-7 py-4 flex items-center justify-center gap-2 transition-transform duration-200 hover:scale-105 active:scale-95 disabled:opacity-60"
          >
            {loading ? "Joining..." : "Notify me"}
            <ArrowRight className="w-4 h-4" />
          </button>
        </motion.form>
      </div>
    </section>
  );
};
